import React, { useCallback, useState } from 'react'
import PropTypes from 'prop-types'
import { TableCell, TableRow } from '../../ui/Table'
import Text from '../../ui/Text'
import { formatDateTime } from '../../../utils/dateHelpers'
import ConfiguredAlertActionsMenu from './ConfiguredAlertActionsMenu'
import EllipsisIcon from '../../ui/icons/EllipsisIcon'

const ROW_CLASS_BASE = 'tw:group tw:hover:bg-gray-100!'
const ROW_CLASS_ACTIVE = 'tw:bg-gray-100!'
const RECIPIENT_CELL_CLASS = 'tw:align-middle!'
const RECIPIENT_INNER_CLASS = 'tw:flex tw:items-center tw:justify-between tw:gap-2 tw:min-h-10'
const ACTIONS_BTN_CLASS = 'tw:bg-transparent tw:border-0 tw:cursor-pointer '
  + 'tw:text-gray-500 tw:rounded! tw:hover:bg-gray-200 tw:opacity-0 '
  + 'tw:group-hover:opacity-100 tw:focus-visible:opacity-100 '
  + 'tw:w-10 tw:h-10 tw:inline-flex tw:items-center tw:justify-center tw:flex-shrink-0 '
  + 'tw:disabled:opacity-50 tw:disabled:cursor-not-allowed'
const PILL_CLASS = 'tw:inline-block tw:max-w-40 tw:truncate tw:rounded! tw:px-2 tw:py-0.5 '
  + 'tw:text-xs tw:font-medium tw:bg-gray-200 tw:text-gray-800'
const RECIPIENT_PILLS_CLASS = 'tw:flex tw:flex-wrap tw:gap-1 tw:min-w-0'
const EMPTY_LABEL = '--'

const renderRecipientPills = recipients => {
  if (!recipients || recipients.length === 0)
    return <span className={PILL_CLASS}>{EMPTY_LABEL}</span>
  return (
    <div className={RECIPIENT_PILLS_CLASS}>
      {recipients.map(user => <span key={user.id} className={PILL_CLASS}>{user.full_name}</span>)}
    </div>
  )
}

const ConfiguredAlertsRow = ({ alert, disabled = false, onDuplicate, onEdit, onDelete }) => {
  // Capture the trigger from the click event, not a ref, to avoid a stale anchor when the button remounts.
  const [actionsBtnEl, setActionsBtnEl] = useState(null)
  const [menuOpen, setMenuOpen] = useState(false)

  const handleMenuToggle = useCallback(event => {
    if (disabled)
      return
    setActionsBtnEl(event.currentTarget)
    setMenuOpen(prev => !prev)
  }, [disabled])
  const handleMenuClose = useCallback(() => setMenuOpen(false), [])
  const handleDuplicate = useCallback(() => onDuplicate(alert), [onDuplicate, alert])
  const handleEdit = useCallback(() => onEdit(alert), [onEdit, alert])
  const handleDelete = useCallback(() => onDelete(alert), [onDelete, alert])

  const rowClass = `${ROW_CLASS_BASE}${menuOpen ? ` ${ROW_CLASS_ACTIVE}` : ''}`
  const actionsBtnClass = `${ACTIONS_BTN_CLASS}${menuOpen ? ' tw:opacity-100!' : ''}`

  return (
    <TableRow className={rowClass}>
      <TableCell><Text className="tw:break-words" lines={2}>{alert.name}</Text></TableCell>
      <TableCell>{formatDateTime(alert.created_at)}</TableCell>
      <TableCell className={RECIPIENT_CELL_CLASS}>
        <div className={RECIPIENT_INNER_CLASS}>
          {renderRecipientPills(alert.recipients)}
          <button
            aria-haspopup="menu"
            aria-label={`Actions pour l’alerte ${alert.name}`}
            className={actionsBtnClass}
            disabled={disabled}
            type="button"
            onClick={handleMenuToggle}
          >
            <EllipsisIcon className="tw:w-4 tw:h-4" />
          </button>
          <ConfiguredAlertActionsMenu
            anchorEl={actionsBtnEl}
            isOpen={menuOpen}
            onClose={handleMenuClose}
            onDelete={handleDelete}
            onDuplicate={handleDuplicate}
            onEdit={handleEdit}
          />
        </div>
      </TableCell>
    </TableRow>
  )
}

ConfiguredAlertsRow.propTypes = {
  alert: PropTypes.shape({
    id: PropTypes.number.isRequired,
    name: PropTypes.string.isRequired
  }).isRequired,
  disabled: PropTypes.bool,
  onDelete: PropTypes.func.isRequired,
  onDuplicate: PropTypes.func.isRequired,
  onEdit: PropTypes.func.isRequired
}

ConfiguredAlertsRow.defaultProps = {
  disabled: false
}

export default ConfiguredAlertsRow
